/**
 * TableServiceBase provides the core state management for SmartTable instances:
 * pagination, sorting, keyword search, eager-loading relations, and date period.
 *
 * Extend this class with TableService (which adds column filters and query assembly).
 * All mutating methods return `this` for chaining.
 */
class TableServiceBase {
  constructor(initialParams = {}) {
    this.initialParams = initialParams;
    this.params = initialParams.params;
    this.searchColumns = initialParams.searchColumns;
    this.searchValue = initialParams.searchValue || "";
    this.filters = initialParams.filters || [];
    this.anotherFilters = initialParams.anotherFilters || [];
    this.filterKeyTransformer = initialParams.filterKeyTransformer;
    this.orderBy = initialParams.orderBy || [];
    this.perPage = initialParams.perPage || 10;
    this.defaultPageSize = initialParams.defaultPageSize || 10;
    this.page = initialParams.page || 1;
    this.with = initialParams.with || [];
    this.total = null;
    this.onFilterChange = initialParams.onFilterChange;
    this.langKeys = [];
    this.period = {
      start: null,
      end: null
    };
  }

  // === Custom Parameters ===

  /** Replaces custom static params merged into the request payload by getFilters(). @returns {TableServiceBase} */
  addCustomParams(params = {}) {
    this.params = { ...params };
    return this;
  }

  // === Search ===

  /** @returns {string[] | undefined} Column names the keyword search queries against. */
  getSearchColumns() {
    return this.searchColumns;
  }

  /** @returns {string} Current keyword search value. */
  getSearchValue() {
    return this.searchValue;
  }

  /** Updates the keyword search value (no filter-change callback). @param {string} searchValue */
  setSearchValue(searchValue) {
    this.searchValue = searchValue;
  }

  // === Sorting ===

  /**
   * Updates the orderBy state based on a user sort interaction.
   * Maps Ant Design's "ascend"/"descend"/undefined to "asc"/"desc"/removal.
   * @param {{ column: string, type: string|undefined }} sortState
   * @returns {TableServiceBase}
   */
  manageOrderBy({ column, type }) {
    const existingOrder = this.orderBy?.some((item) => item.column === column);
    if (existingOrder) {
      this.removeOrderBy(column);
    }

    if (type) {
      const ORDER_TYPE_MAP = { ascend: "asc", descend: "desc" };
      const formattedType = ORDER_TYPE_MAP[type] || type;
      this.orderBy.push({ column, type: formattedType });
    }

    // When defaultOrderBy type is "desc" and user clears it, cycle back to "asc"
    const existingDefaultOrder = (this.initialParams?.defaultOrderBy || []).some((item) => item.column === column);
    if (!type && existingDefaultOrder) {
      this.orderBy.push({ column, type: "asc" });
    }

    return this;
  }

  /** Removes the sort entry for a specific column. @param {string} column @returns {TableServiceBase} */
  removeOrderBy(column) {
    this.orderBy = this.orderBy.filter((item) => item.column !== column);
    return this;
  }

  /** Clears all active sorts. @returns {TableServiceBase} */
  removeAllOrderBy() {
    this.orderBy = [];
    return this;
  }

  // === Pagination ===

  /**
   * Sets the number of rows per page.
   * @param {number} perPage
   * @param {boolean} checkInitial - When true, prefer initialParams.perPage (restores persisted state).
   * @returns {TableServiceBase}
   */
  setPerPage(perPage, checkInitial = false) {
    if (checkInitial) {
      this.perPage = this.initialParams.perPage || perPage || 10;
    } else {
      this.perPage = perPage || 10;
    }
    return this;
  }

  /** Sets the Ant Design `defaultPageSize`. @param {number} defaultPerPage @returns {TableServiceBase} */
  setDefaultPerPage(defaultPerPage) {
    this.defaultPageSize = defaultPerPage || 10;
    return this;
  }

  /** @returns {number} Current rows-per-page value. */
  getPerPage() {
    return this.perPage;
  }

  /** Sets the active page number (1-based). @param {number} page @returns {TableServiceBase} */
  setPage(page) {
    this.page = page || 1;
    return this;
  }

  /** @returns {number} Current page number. */
  getPage() {
    return this.page;
  }

  /** Stores the total record count from the API. @param {number} total @returns {TableServiceBase} */
  setTotal(total) {
    this.total = total || 0;
    return this;
  }

  /** @returns {number|null} Last known total, or null if not yet set. */
  getTotal() {
    return this.total;
  }
}

export default TableServiceBase;
